import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import SummarizeOutlinedIcon from '@mui/icons-material/SummarizeOutlined';
import { normalizeExternalUrl } from 'utils/common';
import {
  buildOwnershipGraph,
  subjectConnectionCount,
} from 'utils/beneficialOwnershipGraph';
import { didSecondLayerRun } from 'utils/beneficialOwnershipLayers';
import { reflowIntoParagraphs } from 'utils/reflowParagraphs';

interface IBeneficialOwnershipTicketResultsProps {
  ticket: any;
  /** Switches the ticket over to the Ownership Tree tab. */
  onViewTree?: () => void;
}

const asArray = (value: any) => (Array.isArray(value) ? value : []);

const cleanText = (value: any, fallback = 'Not available') =>
  typeof value === 'string' && value.trim() ? value.trim() : fallback;

const titleCase = (value: string) =>
  value ? value.charAt(0).toUpperCase() + value.slice(1) : value;

const getWorkflowSnapshot = (ticket: any) => {
  const workflowData = Array.isArray(ticket?.workflowData)
    ? ticket.workflowData[0]
    : ticket?.workflowData || {};

  return {
    workflowData: workflowData || {},
    result: ticket?.beneficialOwnership || workflowData?.result || {},
  };
};

const getRiskTone = (value: any) => {
  const text = String(value || '').toLowerCase();
  if (text.includes('confirm') || text.includes('high') || text.includes('yes')) {
    return 'high';
  }
  if (text.includes('medium') || text.includes('review')) {
    return 'medium';
  }
  if (text.includes('low') || text.includes('none') || text.includes('no')) {
    return 'low';
  }
  return 'medium';
};

// The agent writes risk_level as one of "high" | "medium" | "low" | "info"
// (see the BO final-analysis prompt schema). Ranked so the panel can sort
// worst-first and the executive summary can name the highest level present.
const RISK_RANK: { [key: string]: number } = {
  info: 0,
  low: 1,
  medium: 2,
  high: 3,
};

const getRiskRank = (finding: any) => {
  const level = String(finding?.risk_level || '').trim().toLowerCase();
  return typeof RISK_RANK[level] === 'number' ? RISK_RANK[level] : -1;
};

// A sanctions_flag is only meaningful as a flag when it says something other
// than "nothing found" — the agent uses several spellings for that.
const EMPTY_FLAG_VALUES = [
  '',
  'none',
  'no',
  'not confirmed',
  'not available',
  'n/a',
  'na',
  'null',
  'unknown',
  // The agent's schema constrains sanctions_flag to confirmed|possible|none,
  // but it is still LLM-generated free text and drifts. These spellings all
  // mean "clean" -- treating one as a hit puts a false sanctions warning on an
  // owner, which is the expensive direction to be wrong in.
  'false',
  'no sanctions',
  'not sanctioned',
  'nothing found',
  'no match',
  'no matches',
  'clear',
  'cleared',
  'negative',
];

const isFlagged = (value: any) => {
  const text = String(value || '').trim().toLowerCase();
  return text !== '' && EMPTY_FLAG_VALUES.indexOf(text) === -1;
};

const renderLinkedLabel = (label: string, url?: string) => {
  const safeUrl = normalizeExternalUrl(url);
  if (!safeUrl) {
    return label;
  }

  return (
    <a href={safeUrl} target='_blank' rel='noopener noreferrer'>
      {label}
    </a>
  );
};

const getFindingText = (finding: any) => {
  if (typeof finding === 'string') {
    return finding;
  }

  return (
    finding?.finding ||
    finding?.description ||
    finding?.summary ||
    finding?.title ||
    'Finding saved with this investigation.'
  );
};

const getFindingTitle = (finding: any, index: number) =>
  typeof finding === 'string'
    ? `Finding ${index + 1}`
    : finding?.title || finding?.risk_type || `Finding ${index + 1}`;

interface IOwnerRow {
  name: string;
  relationship: string;
  ownership: string;
  confidence: string;
  sanctionsFlag: string;
  sourceUrl?: string;
}

// owners[] and ownership_findings[] describe the same thing with different
// key names — normalise both into one row shape so either can back the table.
const normalizeOwner = (raw: any): IOwnerRow => {
  const percentage = raw?.ownership_percentage;
  const percentageText =
    percentage === null || percentage === undefined || percentage === ''
      ? cleanText(raw?.ownership, '—')
      : String(percentage).indexOf('%') === -1
      ? `${percentage}%`
      : String(percentage);

  return {
    name: cleanText(raw?.name, 'Unknown owner'),
    relationship: cleanText(
      raw?.relationship_to_subject || raw?.relation || raw?.relationship,
      'Not specified',
    ),
    ownership: percentageText,
    confidence: cleanText(raw?.confidence, ''),
    sanctionsFlag: cleanText(raw?.sanctions_flag, ''),
    sourceUrl: raw?.source_url || undefined,
  };
};

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

interface IBoScrollBlockProps {
  label: string;
  children: React.ReactNode;
}

/**
 * The one scrolling container every long section uses.
 *
 * The wrapper owns the two fades and the scroller owns the overflow, so a
 * fade sits over the content instead of scrolling away with it. Each fade is
 * only on when there is genuinely content in that direction, which means a
 * section short enough to fit shows no scrollbar and no fade at all.
 *
 * It deliberately does not reuse `.ieepa-info-modal-scroll` for the scrollbar
 * styling: that class is shared with the IEEPA info modal, and restyling it
 * from here would reach a different tool.
 */
const BoScrollBlock: React.FC<IBoScrollBlockProps> = ({ label, children }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [hasAbove, setHasAbove] = useState(false);
  const [hasBelow, setHasBelow] = useState(false);

  const measure = useCallback(() => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }
    const overflowing = el.scrollHeight - el.clientHeight > 4;
    setHasAbove(overflowing && el.scrollTop > 4);
    setHasBelow(
      overflowing && el.scrollHeight - el.scrollTop - el.clientHeight > 4,
    );
  }, []);

  // Re-measure when the box or its content resizes — filtering a list or a
  // late-arriving font both change the overflow without firing a scroll.
  useEffect(() => {
    measure();

    const el = scrollRef.current;
    const Observer = (window as any).ResizeObserver;
    if (!el || typeof Observer !== 'function') {
      return undefined;
    }

    const observer = new Observer(measure);
    observer.observe(el);
    if (el.firstElementChild) {
      observer.observe(el.firstElementChild);
    }
    return () => observer.disconnect();
  }, [measure, children]);

  useEffect(() => {
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [measure]);

  const wrapClass = [
    'bo-ticket-scroll-wrap',
    hasAbove ? 'has-above' : '',
    hasBelow ? 'has-below' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={wrapClass}>
      <div
        className='bo-ticket-scroll'
        ref={scrollRef}
        onScroll={measure}
        tabIndex={0}
        role='region'
        aria-label={label}
      >
        {children}
      </div>
    </div>
  );
};

const BeneficialOwnershipTicketResults: React.FC<
  IBeneficialOwnershipTicketResultsProps
> = ({ ticket, onViewTree }) => {
  const { workflowData, result } = getWorkflowSnapshot(ticket);

  const entityName =
    workflowData?.entity_name ||
    ticket?.commodity?.commodityName ||
    ticket?.classificationValue ||
    ticket?.ticketName ||
    'Entity';

  // owners[] and ownership_findings[] are two views of the same ownership
  // graph and a run can populate either one. Falling back stops the panel
  // (and the "Owners Found" tile) reading zero on runs where the agent only
  // filled ownership_findings.
  const rawOwners = asArray(result?.owners);
  const ownershipFindings = asArray(result?.ownership_findings);
  const ownerSource = rawOwners.length ? rawOwners : ownershipFindings;
  const owners = useMemo(
    () => ownerSource.map(normalizeOwner),
    [ownerSource],
  );
  const ownersFromFindings = !rawOwners.length && ownershipFindings.length > 0;
  const showConfidenceColumn = owners.some((owner) => Boolean(owner.confidence));
  const flaggedOwners = owners.filter((owner) => isFlagged(owner.sanctionsFlag));

  const associatedEntities = asArray(result?.associated_entities);
  const links = asArray(result?.supporting_links);

  const findings = useMemo(() => {
    const list = asArray(result?.key_findings).slice();
    // Worst first — an analyst reading top-down should hit the high-risk
    // findings before the informational ones.
    list.sort((a: any, b: any) => getRiskRank(b) - getRiskRank(a));
    return list;
  }, [result]);
  const highFindings = findings.filter(
    (finding: any) => getRiskRank(finding) === RISK_RANK.high,
  );
  const topFinding = findings.length ? findings[0] : null;
  const topFindingRank = topFinding ? getRiskRank(topFinding) : -1;
  const topFindingLevel =
    topFindingRank >= 0
      ? titleCase(String(topFinding?.risk_level || '').trim().toLowerCase())
      : '';

  const jurisdictionRisk = result?.jurisdiction_risk || {};
  const highRiskJurisdictions = asArray(
    jurisdictionRisk?.high_risk_jurisdictions,
  ).filter((item: any) => typeof item === 'string' && item.trim());
  const jurisdictionNote = cleanText(jurisdictionRisk?.risk_note, '');

  const sanctionsFlag = cleanText(result?.sanctions_flag, 'Not available');
  const riskTone = getRiskTone(sanctionsFlag);
  // Derived from the saved workflow state, not the intake answer — a
  // background run enables the second layer server-side and the stored
  // boolean stays false. See utils/beneficialOwnershipLayers.
  const secondLayer = didSecondLayerRun(ticket);

  // The Ownership Tree tab only exists when a graph was saved, so the link
  // to it is gated on exactly the same check Ticket.tsx uses.
  // The badge counts the subject's direct connections -- the same number the
  // tree's subject card shows when the tab opens.
  const ownershipGraph = useMemo(() => buildOwnershipGraph(ticket), [ticket]);
  const showTreeLink = !ownershipGraph.isEmpty;
  const treeConnectionCount = subjectConnectionCount(ownershipGraph);

  const summary =
    result?.general_description ||
    result?.summary ||
    result?.beneficial_ownership_summary ||
    'No final summary was saved with this investigation.';
  // The saved summary comes back as one continuous wall of text with no
  // embedded line breaks — reflow it into paragraphs for readability,
  // matching the PDF download's treatment of the same field.
  const summaryParagraphs = useMemo(
    () => reflowIntoParagraphs(summary),
    [summary],
  );
  const summaryWordCount = useMemo(
    () => String(summary).split(/\s+/).filter(Boolean).length,
    [summary],
  );

  // executive_summary is written by the agent as 3-6 plain-language sentences
  // covering the subject, the ownership picture, the highest risk finding and
  // the overall conclusion. It is short by contract, so the block that shows
  // it never scrolls. Where a run predates the field, the opening of the full
  // narrative stands in for it.
  const executiveSummary = cleanText(result?.executive_summary, '');
  const execParagraphs = useMemo(
    () =>
      executiveSummary
        ? reflowIntoParagraphs(executiveSummary)
        : summaryParagraphs.slice(0, 2),
    [executiveSummary, summaryParagraphs],
  );

  const sanctionsSummaryParagraphs = useMemo(
    () =>
      reflowIntoParagraphs(
        cleanText(
          result?.sanctions_summary,
          'No sanctions summary was saved with this investigation.',
        ),
      ),
    [result],
  );

  const [entityFilter, setEntityFilter] = useState('');
  const [linkFilter, setLinkFilter] = useState('');

  const filteredEntities = useMemo(() => {
    const query = entityFilter.trim().toLowerCase();
    if (!query) {
      return associatedEntities;
    }
    return associatedEntities.filter((entity: any) =>
      `${entity?.name || ''} ${entity?.relation || ''} ${entity?.notes || ''}`
        .toLowerCase()
        .includes(query),
    );
  }, [associatedEntities, entityFilter]);

  const filteredLinks = useMemo(() => {
    const query = linkFilter.trim().toLowerCase();
    if (!query) {
      return links;
    }
    return links.filter((link: any) =>
      `${link?.title || ''} ${link?.supports || ''} ${link?.source_type || ''}`
        .toLowerCase()
        .includes(query),
    );
  }, [links, linkFilter]);

  // Section jumps scroll by ref rather than a `#hash` href so the router's
  // location is never touched by a purely in-page navigation.
  const sectionRefs = useRef<{ [key: string]: HTMLElement | null }>({});
  const registerSection = (key: string) => (node: HTMLElement | null) => {
    sectionRefs.current[key] = node;
  };
  const scrollToSection = (key: string) => () => {
    const node = sectionRefs.current[key];
    if (node && typeof node.scrollIntoView === 'function') {
      node.scrollIntoView({
        behavior: prefersReducedMotion() ? 'auto' : 'smooth',
        block: 'start',
      });
    }
  };

  return (
    <div className='bo-ticket-shell'>
      {/* --------------------------------------------------------- stat row */}
      <div className='bo-ticket-stat-grid'>
        <button
          type='button'
          className='bo-ticket-stat-card'
          onClick={scrollToSection('ownership')}
        >
          <span>
            <GroupsOutlinedIcon sx={{ fontSize: 18 }} />
            Owners Found
          </span>
          <strong>
            {owners.length}
            {ownersFromFindings && <em>from detailed findings</em>}
          </strong>
        </button>
        <button
          type='button'
          className='bo-ticket-stat-card'
          onClick={scrollToSection('findings')}
        >
          <span>
            <ReportProblemOutlinedIcon sx={{ fontSize: 18 }} />
            Key Findings
          </span>
          <strong>
            {findings.length}
            {highFindings.length > 0 && <em>{highFindings.length} high</em>}
          </strong>
        </button>
        <button
          type='button'
          className='bo-ticket-stat-card'
          onClick={scrollToSection('entities')}
        >
          <span>
            <AccountTreeOutlinedIcon sx={{ fontSize: 18 }} />
            Associated Entities
          </span>
          <strong>{associatedEntities.length}</strong>
        </button>
        <button
          type='button'
          className='bo-ticket-stat-card'
          onClick={scrollToSection('sources')}
        >
          <span>
            <LinkOutlinedIcon sx={{ fontSize: 18 }} />
            Supporting Links
          </span>
          <strong>{links.length}</strong>
        </button>
      </div>

      {/* ------------------------------------------------ executive summary */}
      <section
        className='bo-ticket-exec'
        ref={registerSection('exec') as any}
      >
        <div className='bo-ticket-eyebrow'>
          <AccountTreeOutlinedIcon sx={{ fontSize: 16 }} />
          Executive Summary
        </div>
        <h2>{entityName}</h2>
        <div className='bo-ticket-meta-row'>
          <span
            className={`bo-ticket-flag-pill${
              riskTone === 'high' ? ' high' : ''
            }`}
          >
            Sanctions: {sanctionsFlag}
          </span>
          {ownersFromFindings && (
            <>
              <i className='bo-ticket-meta-dot' />
              <span>Ownership from detailed findings</span>
            </>
          )}
        </div>

        <div className='bo-ticket-verdict'>
          <div className='bo-ticket-verdict-cell'>
            <span className='bo-lbl'>Sanctions posture</span>
            <span
              className={`bo-val${riskTone === 'high' ? ' high' : ''}`}
            >
              {titleCase(sanctionsFlag)}
            </span>
            <span className='bo-sub'>
              {secondLayer
                ? 'Second-layer search included'
                : 'First-layer search only'}
            </span>
          </div>
          <div className='bo-ticket-verdict-cell'>
            <span className='bo-lbl'>Highest finding</span>
            <span
              className={`bo-val${
                topFindingRank === RISK_RANK.high ? ' high' : ''
              }`}
            >
              {topFindingLevel || '—'}
              {findings.length > 0 && topFindingRank === RISK_RANK.high && (
                <em>
                  {highFindings.length} of {findings.length}
                </em>
              )}
            </span>
            <span className='bo-sub'>
              {topFinding
                ? getFindingTitle(topFinding, 0)
                : 'No findings recorded'}
            </span>
          </div>
          <div className='bo-ticket-verdict-cell'>
            <span className='bo-lbl'>Ownership flagged</span>
            <span
              className={`bo-val${flaggedOwners.length ? ' high' : ''}`}
            >
              {flaggedOwners.length}
              {owners.length > 0 && <em>of {owners.length}</em>}
            </span>
            <span className='bo-sub'>
              {flaggedOwners.length
                ? flaggedOwners[0].name
                : owners.length
                ? 'None flagged'
                : 'No ownership records'}
            </span>
          </div>
          <div className='bo-ticket-verdict-cell'>
            <span className='bo-lbl'>High-risk jurisdictions</span>
            <span className='bo-val'>{highRiskJurisdictions.length}</span>
            <span className='bo-sub'>
              {highRiskJurisdictions.length
                ? highRiskJurisdictions.join(', ')
                : 'None recorded'}
            </span>
          </div>
        </div>

        <div className='bo-ticket-exec-prose'>
          {execParagraphs.length ? (
            execParagraphs.map((paragraph, index) => (
              <p key={index}>{paragraph}</p>
            ))
          ) : (
            <p>No executive summary was saved with this investigation.</p>
          )}
        </div>

        <nav className='bo-ticket-jumps'>
          <span className='bo-ticket-jumps-label'>Jump to</span>
          <button
            type='button'
            className='bo-ticket-jump'
            onClick={scrollToSection('sanctions')}
          >
            Sanctions summary
          </button>
          <button
            type='button'
            className='bo-ticket-jump'
            onClick={scrollToSection('findings')}
          >
            Risk findings
          </button>
          <button
            type='button'
            className='bo-ticket-jump'
            onClick={scrollToSection('narrative')}
          >
            Full summary
          </button>
          <button
            type='button'
            className='bo-ticket-jump'
            onClick={scrollToSection('ownership')}
          >
            Ownership found
          </button>
          <button
            type='button'
            className='bo-ticket-jump'
            onClick={scrollToSection('entities')}
          >
            Associated entities
          </button>
          <button
            type='button'
            className='bo-ticket-jump'
            onClick={scrollToSection('sources')}
          >
            Supporting links
          </button>
        </nav>
      </section>

      {/* ------------------------------- row A: sanctions | risk findings */}
      <div className='bo-ticket-row bo-ticket-row-a'>
        <section
          className='bo-ticket-panel'
          ref={registerSection('sanctions') as any}
        >
          <div className='bo-ticket-panel-heading'>
            <ShieldOutlinedIcon sx={{ fontSize: 19 }} />
            <div className='bo-ticket-panel-heading-text'>
              <h3>Sanctions Summary</h3>
              <p>Sanctions and adverse public information assessment.</p>
            </div>
          </div>
          <div
            className={`bo-ticket-status-callout${
              riskTone === 'high' ? ' high' : ''
            }`}
          >
            <ErrorOutlineOutlinedIcon sx={{ fontSize: 17 }} />
            <span>{sanctionsFlag}</span>
          </div>
          <BoScrollBlock label='Sanctions summary'>
            <div>
              {sanctionsSummaryParagraphs.map((paragraph, index) => (
                <p className='bo-ticket-body-text' key={index}>
                  {paragraph}
                </p>
              ))}
              {highRiskJurisdictions.length > 0 && (
                <>
                  <div className='bo-ticket-sub-label'>
                    High-risk jurisdictions in the chain
                  </div>
                  <div className='bo-ticket-chips'>
                    {highRiskJurisdictions.map((jurisdiction: string) => (
                      <span key={jurisdiction}>{jurisdiction}</span>
                    ))}
                  </div>
                </>
              )}
              {jurisdictionNote && (
                <p className='bo-ticket-body-text'>{jurisdictionNote}</p>
              )}
            </div>
          </BoScrollBlock>
        </section>

        <section
          className='bo-ticket-panel'
          ref={registerSection('findings') as any}
        >
          <div className='bo-ticket-panel-heading'>
            <ReportProblemOutlinedIcon sx={{ fontSize: 19 }} />
            <div className='bo-ticket-panel-heading-text'>
              <h3>Risk Findings</h3>
              <p>Highlighted issues, highest severity first.</p>
            </div>
            {findings.length > 0 && (
              <span className='bo-ticket-count-pill'>{findings.length}</span>
            )}
          </div>
          {findings.length ? (
            <BoScrollBlock label='Risk findings'>
              <div className='bo-ticket-finding-list'>
                {findings.map((finding: any, index: number) => {
                  const level = String(finding?.risk_level || '')
                    .trim()
                    .toLowerCase();
                  return (
                    <article
                      key={`${getFindingTitle(finding, index)}-${index}`}
                      className='bo-ticket-finding'
                    >
                      <div>
                        <strong>{getFindingTitle(finding, index)}</strong>
                        {level && (
                          <span
                            className={`bo-ticket-risk-level${
                              level === 'high' ? ' high' : ''
                            }`}
                          >
                            {level}
                          </span>
                        )}
                      </div>
                      <p>{getFindingText(finding)}</p>
                    </article>
                  );
                })}
              </div>
            </BoScrollBlock>
          ) : (
            <div className='bo-ticket-empty'>No key findings were saved.</div>
          )}
        </section>
      </div>

      {/* ------------------------------ row B: full summary | ownership */}
      <div className='bo-ticket-row bo-ticket-row-b'>
        <section
          className='bo-ticket-panel'
          ref={registerSection('narrative') as any}
        >
          <div className='bo-ticket-panel-heading'>
            <SummarizeOutlinedIcon sx={{ fontSize: 19 }} />
            <div className='bo-ticket-panel-heading-text'>
              <h3>Full Summary</h3>
              <p>The complete written analysis saved with this investigation.</p>
            </div>
            {summaryWordCount > 0 && (
              <span className='bo-ticket-count-pill'>
                ~{summaryWordCount} words
              </span>
            )}
          </div>
          <BoScrollBlock label='Full summary'>
            <div>
              {summaryParagraphs.map((paragraph, index) => (
                <p className='bo-ticket-body-text' key={index}>
                  {paragraph}
                </p>
              ))}
            </div>
          </BoScrollBlock>
        </section>

        <section
          className='bo-ticket-panel'
          ref={registerSection('ownership') as any}
        >
          <div className='bo-ticket-panel-heading'>
            <GroupsOutlinedIcon sx={{ fontSize: 19 }} />
            <div className='bo-ticket-panel-heading-text'>
              <div className='bo-ticket-heading-title-row'>
                <h3>Ownership Found</h3>
                {owners.length > 0 && (
                  <span className='bo-ticket-count-pill'>
                    {owners.length} owner{owners.length === 1 ? '' : 's'}
                  </span>
                )}
              </div>
              <p>
                {showTreeLink
                  ? 'Owners and shareholders only. The tree also shows officers, directors, and other connections.'
                  : 'Saved owners and shareholder relationships from the workflow.'}
              </p>
            </div>
            {showTreeLink && (
              <button
                type='button'
                className='bo-ticket-tree-link'
                onClick={onViewTree}
              >
                <AccountTreeOutlinedIcon sx={{ fontSize: 15 }} />
                View relationship tree
                <span className='bo-ticket-tree-link-count'>
                  {treeConnectionCount}
                </span>
              </button>
            )}
          </div>
          {owners.length ? (
            <BoScrollBlock label='Ownership records'>
              <table className='bo-ticket-owner-table'>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Relationship</th>
                    <th>Ownership</th>
                    {showConfidenceColumn && <th>Confidence</th>}
                  </tr>
                </thead>
                <tbody>
                  {owners.map((owner, index) => (
                    <tr key={`${owner.name}-${index}`}>
                      <td className='bo-owner-name'>
                        {renderLinkedLabel(owner.name, owner.sourceUrl)}
                      </td>
                      <td>{owner.relationship}</td>
                      <td className='bo-owner-pct'>{owner.ownership}</td>
                      {showConfidenceColumn && (
                        <td className='bo-owner-conf'>
                          {titleCase(owner.confidence) || '—'}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </BoScrollBlock>
          ) : (
            <div className='bo-ticket-empty'>
              No ownership records were saved.
            </div>
          )}
        </section>
      </div>

      {/* --------------------------- row C: entities | supporting links */}
      <div className='bo-ticket-row bo-ticket-row-c'>
        <section
          className='bo-ticket-panel'
          ref={registerSection('entities') as any}
        >
          <div className='bo-ticket-panel-heading'>
            <AccountTreeOutlinedIcon sx={{ fontSize: 19 }} />
            <div className='bo-ticket-panel-heading-text'>
              <h3>Associated Entities</h3>
              <p>Connected parties identified during research.</p>
            </div>
            {associatedEntities.length > 8 && (
              <input
                className='bo-ticket-filter'
                type='search'
                value={entityFilter}
                placeholder='Filter…'
                aria-label='Filter associated entities'
                onChange={(event) => setEntityFilter(event.target.value)}
              />
            )}
            {associatedEntities.length > 0 && (
              <span className='bo-ticket-count-pill'>
                {entityFilter.trim()
                  ? `${filteredEntities.length} of ${associatedEntities.length}`
                  : associatedEntities.length}
              </span>
            )}
          </div>
          {associatedEntities.length ? (
            <BoScrollBlock label='Associated entities'>
              {filteredEntities.length ? (
                <ul className='bo-ticket-entity-list'>
                  {filteredEntities.map((entity: any, index: number) => (
                    <li key={`${entity?.name || 'entity'}-${index}`}>
                      <div className='bo-ticket-list-row'>
                        <strong>
                          {renderLinkedLabel(
                            cleanText(entity?.name, 'Unknown entity'),
                            entity?.source_url,
                          )}
                        </strong>
                        {isFlagged(entity?.sanctions_flag) && (
                          <span className='bo-ticket-source-tag'>
                            {entity.sanctions_flag}
                          </span>
                        )}
                      </div>
                      <span>
                        {cleanText(
                          entity?.relation,
                          'Relationship not specified',
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className='bo-ticket-empty'>
                  No entities match that filter.
                </div>
              )}
            </BoScrollBlock>
          ) : (
            <div className='bo-ticket-empty'>
              No associated entities were saved.
            </div>
          )}
        </section>

        <section
          className='bo-ticket-panel'
          ref={registerSection('sources') as any}
        >
          <div className='bo-ticket-panel-heading'>
            <ArticleOutlinedIcon sx={{ fontSize: 19 }} />
            <div className='bo-ticket-panel-heading-text'>
              <h3>Supporting Links</h3>
              <p>Sources retained from the final workflow response.</p>
            </div>
            {links.length > 8 && (
              <input
                className='bo-ticket-filter'
                type='search'
                value={linkFilter}
                placeholder='Filter…'
                aria-label='Filter supporting links'
                onChange={(event) => setLinkFilter(event.target.value)}
              />
            )}
            {links.length > 0 && (
              <span className='bo-ticket-count-pill'>
                {linkFilter.trim()
                  ? `${filteredLinks.length} of ${links.length}`
                  : links.length}
              </span>
            )}
          </div>
          {links.length ? (
            <BoScrollBlock label='Supporting links'>
              {filteredLinks.length ? (
                <ul className='bo-ticket-source-list'>
                  {filteredLinks.map((link: any, index: number) => (
                    <li key={`${link?.title || 'source'}-${index}`}>
                      <div className='bo-ticket-list-row'>
                        <strong>
                          {renderLinkedLabel(
                            cleanText(link?.title, 'Source'),
                            link?.url,
                          )}
                        </strong>
                        {link?.source_type && (
                          <span className='bo-ticket-source-tag'>
                            {link.source_type}
                          </span>
                        )}
                      </div>
                      {link?.supports && <span>{link.supports}</span>}
                    </li>
                  ))}
                </ul>
              ) : (
                <div className='bo-ticket-empty'>
                  No sources match that filter.
                </div>
              )}
            </BoScrollBlock>
          ) : (
            <div className='bo-ticket-empty'>
              No supporting links were saved.
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default BeneficialOwnershipTicketResults;
